import React from "react";
import { Box, Typography, Avatar, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import { Call as CallIconsax } from "iconsax-react";
import { nameToContactAvatarImage } from "../../../config/AvatarColor";
import PlatformIcon from "../../../components/Inbox/Features/PlatformIcon";
import { actionBtnSx, actionIconBoxSx } from "../../../utils/callActionButtonStyles";
import IconButtonWithTooltip from "../../../components/Inbox/Shared/IconButtonWithTooltip";

/**
 * A transfer offered TO this agent — visually similar to CallWidget's
 * ringing state (same corner, same slide-in), but a distinct component
 * rather than a third branch inside CallWidget: the two can never be shown
 * at once (see callSlice's incomingTransfer comment for why), and mixing
 * "brand new call" and "transfer of an existing call" into one component's
 * conditionals would make CallWidget harder to follow for no real reuse
 * benefit — this only shares maybe three style rules with it.
 *
 * Redesigned 2026-08-20 to match CallWidget.jsx's redesign (Rahim: position,
 * icons, buttons were left behind when only CallWidget got fixed): real
 * contact avatar + platform badge instead of a flat initials circle,
 * repositioned below the app's top nav instead of the viewport corner, and
 * the same square Answer/Decline button style (callActionButtonStyles.js).
 * The "Transfer from {agent}" label is kept — that part was already right.
 */
export default function TransferRequestWidget({ transfer, onAccept, onDecline }) {
  const theme = useTheme();
  if (!transfer) return null;

  const displayName = transfer.contactName || transfer.waNumber;
  const initials = (displayName || "?").slice(0, 2).toUpperCase();

  return (
    <Box
      role="dialog"
      aria-label="Incoming transfer"
      sx={{
        position: "fixed",
        // Same offset as CallWidget.jsx — see its own comment for why this
        // isn't route-aware (this widget is global too).
        top: { xs: 76, sm: 80 },
        right: { xs: 12, sm: 16 },
        left: { xs: 12, sm: "auto" },
        zIndex: theme.zIndex.snackbar + 1,
        width: { xs: "auto", sm: 336 },
        maxWidth: 400,
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: theme.palette.mode === "dark" ? "#1c2320" : "#ffffff",
        boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        animation: "transferWidgetSlideIn 260ms cubic-bezier(0.16, 1, 0.3, 1)",
        "@keyframes transferWidgetSlideIn": {
          from: { opacity: 0, transform: "translateY(-12px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.75 }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box
            sx={{
              position: "absolute",
              inset: -4,
              borderRadius: "50%",
              border: "2px solid #1f7a6c",
              animation: "transferWidgetPulse 1.6s ease-out infinite",
              "@keyframes transferWidgetPulse": {
                "0%": { transform: "scale(0.9)", opacity: 0.9 },
                "100%": { transform: "scale(1.35)", opacity: 0 },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
          {/* Same avatar + platform-badge convention as CallWidget.jsx —
              real contact image (falls back to initials automatically) plus
              the WhatsApp badge, instead of a flat-color initials circle. */}
          <Avatar src={nameToContactAvatarImage(displayName)} sx={{ width: 44, height: 44, bgcolor: "#1f7a6c", fontSize: 16, fontWeight: 600 }}>
            {initials}
          </Avatar>
          {/* display:flex — same fix as CallWidget.jsx, see its comment for why. */}
          <Box sx={{ position: "absolute", bottom: -2, right: -2, borderRadius: "50%", border: `2px solid ${theme.palette.mode === "dark" ? "#1c2320" : "#ffffff"}`, display: "flex" }}>
            <PlatformIcon platform="whatsapp" size={16} sx={{ ml: 0 }} />
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PhoneForwardedIcon sx={{ fontSize: 13, color: "#1f7a6c" }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "#1f7a6c" }}>
              Transfer from {transfer.fromAgentName || "an agent"}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
            {displayName}
          </Typography>
        </Box>

        {/* Same square Answer/Decline buttons as CallWidget.jsx's ringing
            state (callActionButtonStyles.js) — was a round, differently-
            styled pair here, matching what CallWidget used to look like
            before that got fixed. */}
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <IconButtonWithTooltip label="Decline transfer" onClick={onDecline} size="small" sx={actionBtnSx(theme.palette.error.main, false)} aria-label="Decline transfer">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButtonWithTooltip>
          <IconButtonWithTooltip label="Accept transfer" onClick={onAccept} size="small" sx={actionBtnSx(theme.palette.success.main, true)} aria-label="Accept transfer">
            <Box sx={actionIconBoxSx}><CallIconsax size="100%" color="currentColor" variant="Bold" /></Box>
          </IconButtonWithTooltip>
        </Box>
      </Box>
    </Box>
  );
}
